<template>
    <div class="input" :data-label="label">
        <a href="#" @click.prevent="getHelp" class="help-text" v-if="helpText">{{ helpText }}</a>
        <label v-if="label">{{ label }}</label>
        <textarea
            v-bind="$attrs"
            :class="inputClass"
            v-if="isTextarea"
            v-model="boundModelValue"
            :rows="rows || 4"
            style="width: 100%"
            v-on="$listeners"
            @focus="focus"
            @blur="blur"
            :placeholder="placeholder"
        ></textarea>
        <div class="input-container" v-else>
            <input
                v-bind="$attrs"
                :class="inputClass"
                v-on="$listeners"
                v-on:keyup="keyup"
                @focus="focus"
                @blur="blur"
                :type="effectiveType"
                v-model="boundModelValue"
                ref="input"
                :placeholder="placeholder"
            />
            <slot name="input-append" />
            <span class="show-hide material-icons unselectable" v-if="canShow" @click="showPassword">
                visibility_off
            </span>
            <span class="show-hide material-icons unselectable" v-if="canHide" @click="hidePassword"> visibility </span>
        </div>
        <div class="input-error" v-for="(errorMessage, index) in errorMessages" :key="index">{{ errorMessage }}</div>
    </div>
</template>
<script>
function toLocalIso8601DateTime(date) {
    if (!date) {
        return date
    }

    date = new Date(date)

    const year = date.getFullYear()
    const month = (date.getMonth() + 1).toString().padStart(2, '0')
    const day = date.getDate().toString().padStart(2, '0')
    const hour = date.getHours().toString().padStart(2, '0')
    const minute = date.getMinutes().toString().padStart(2, '0')
    const second = date.getSeconds().toString().padStart(2, '0')

    return `${year}-${month}-${day}T${hour}:${minute}:${second}`
}

const ADAPTERS = {
    date: {
        get: value => (value ? new Date(value).toISOString().substring(0, 10) : null),
        set: value => (value === '' ? null : value),
    },
    'datetime-local': {
        get: value => (value ? toLocalIso8601DateTime(value) : value),
        set: value => (value ? new Date(value).toISOString() : null),
    },
    number: {
        get: value => value,
        set: value => (value?.trim && value?.trim() == '' ? null : value),
    },
}

const DEFAULT_ADAPTER = {
    get: value => value,
    set: value => value,
}

export default {
    name: 'MtInput',
    model: {
        prop: 'modelValue',
        event: 'update:modelValue',
    },
    props: {
        label: String,
        modelValue: [String, Number],
        errorMessage: [String, Array],
        placeholder: String,
        type: String,
        helpText: String,
        rows: Number,
    },
    data() {
        return {
            showingPassword: false,
            valid: true,
        }
    },
    computed: {
        adapter() {
            return ADAPTERS[this.type] ?? DEFAULT_ADAPTER
        },
        hasError() {
            return !!this.errorMessage || !this.valid
        },
        isTextarea() {
            return this.type == 'textarea'
        },
        isPassword() {
            return this.type == 'password'
        },
        canShow() {
            return this.isPassword && !this.showingPassword
        },
        canHide() {
            return this.isPassword && this.showingPassword
        },
        effectiveType() {
            return this.isPassword ? this.passwordType : this.type
        },
        passwordType() {
            return this.showingPassword ? 'text' : 'password'
        },
        boundModelValue: {
            get() {
                return this.adapter.get(this.modelValue)
            },
            set(value) {
                this.checkValidity()
                this.$emit('update:modelValue', this.adapter.set(value))
            },
        },
        inputClass() {
            return { 'has-error': this.hasError }
        },
        errorMessages() {
            if (!this.errorMessage) {
                return []
            }

            return Array.isArray(this.errorMessage) ? this.errorMessage : [this.errorMessage]
        },
    },
    methods: {
        checkValidity() {
            if (this.$refs.input) {
                this.valid = this.$refs.input.validity.valid
            }
        },
        showPassword() {
            this.showingPassword = true
        },
        hidePassword() {
            this.showingPassword = false
        },
        getHelp() {
            this.$emit('help')
        },
        keyup(e) {
            this.checkValidity()

            if (e.keyCode == 13) {
                this.$emit('enter')
            }
        },
        focus() {
            this.checkValidity()
            this.$emit('focus')
        },
        blur() {
            this.checkValidity()
            this.$emit('blur')
        },
    },
}
</script>
<style scoped>
.code textarea {
    font-family: monospace;
}
</style>
